import { Children, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from 'src/components/PageHeader';
import {
  FlexibleStepperContextProps,
  FlexibleStepperHeader,
  FlexibleStepperProps,
} from './types';
import { FlexibleStepperContext } from './useFlexibleStepper';
function FlexibleStepper({
  children,
  persistentFooter,
  persistentHeader,
  initialStep = 0,
}: FlexibleStepperProps) {
  const [step, setStep] = useState(initialStep);
  const primitiveChildren = Children.toArray(children);
  const currentStepChild = primitiveChildren[step];
  const isFirstStep = step === initialStep;
  const isLastStep = step === primitiveChildren.length - 1;

  useEffect(() => {
    const scrollTimeOut = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);

    return () => {
      clearTimeout(scrollTimeOut);
    };
  }, [step]);

  const moveForward = () => {
    if (step < primitiveChildren.length - 1) {
      setStep((prevState: number) => prevState + 1);
    }
  };

  const moveBackward = () => {
    if (step > initialStep) {
      setStep((prevState: number) => prevState - 1);
    }
  };

  const restoreTo = (moveTo: number, callback?: CallableFunction) => {
    setStep(moveTo);

    if (callback) {
      callback();
    }
  };

  const contextValue: FlexibleStepperContextProps = {
    step,
    moveForward,
    moveBackward,
    restoreTo,
    isFirstStep,
    isLastStep,
    totalSteps: primitiveChildren.length,
    initialStep,
  };

  return (
    <FlexibleStepperContext.Provider value={contextValue}>
      {persistentHeader ? persistentHeader(contextValue) : null}
      {currentStepChild}
      {persistentFooter ? persistentFooter(contextValue) : null}
    </FlexibleStepperContext.Provider>
  );
}

export default FlexibleStepper;

const DefaultHeader = ({
  isFirstStep,
  moveBackward,
  title,
}: FlexibleStepperHeader) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (isFirstStep) {
      navigate(-1);
    } else {
      moveBackward();
    }
  };

  return <PageHeader title={title} onBack={handleBack} />;
};

FlexibleStepper.DefaultHeader = DefaultHeader;
